import { useCallback, useEffect, useState } from 'react';
import { useRouter } from '@frontend/weave-router';

type useLeaveConfirmProps = {
  shouldBlock: boolean;
  onBlockHandler: () => void;
  allowedRoutePattern?: RegExp;
};

type Location = {
  pathname: string;
  search: string;
  hash: string;
  state?: any;
};

export const useLeaveConfirmation = ({ shouldBlock, onBlockHandler, allowedRoutePattern }: useLeaveConfirmProps) => {
  const router = useRouter();

  const [nextLocation, setNextLocation] = useState<Location | undefined>();
  const [canNavigate, setCanNavigate] = useState(!shouldBlock);

  // Fallback to legacy location if weave-router is not available for current route. Not all routes are using weave-router yet.
  const currentPath = router.history.location.pathname;

  const handleNavigateBlock = (nextLocation: Location) => {
    setNextLocation(nextLocation);
    const isNextLocationAllowed = allowedRoutePattern?.test(nextLocation.pathname);
    if (isNextLocationAllowed || canNavigate) {
      setCanNavigate(true);
      return false;
    }
    onBlockHandler();
    return true;
  };

  useEffect(() => {
    if (canNavigate) return;

    // New weave-router blocking
    const unblockWeaveRoute = router.history.block({
      blockerFn: (transition: any) => {
        return handleNavigateBlock(transition.nextLocation);
      },
      enableBeforeUnload: true,
    });

    return () => {
      unblockWeaveRoute();
    };
  }, [currentPath, canNavigate]);

  // Unblock navigation after confirmation using canNavigate.
  useEffect(() => {
    if (canNavigate && nextLocation?.pathname) {
      let nextPath = nextLocation.pathname;
      // TODO: Check if we need to include weave-router's history here. Though this is working good for now.
      if (nextLocation?.search && nextLocation.search !== '') nextPath += nextLocation.search;
      router.history.push(nextPath);
    }
  }, [canNavigate, nextLocation?.pathname]);

  const allowNavigation = useCallback(() => {
    setCanNavigate(true);
  }, []);

  const blockNavigation = useCallback(() => {
    setCanNavigate(false);
  }, []);

  return {
    allowNavigation,
    blockNavigation,
  };
};
